import {html, css, LitElement} from 'lit'
import { classMap } from 'lit/directives/class-map.js'
import {customElement, property, query, state} from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { TIAmie } from './wrappers/iamie'
import GridCSS from './styles/grid'
import './styles/iconlibrary'
import '@shoelace-style/shoelace/dist/components/input/input'
import '@shoelace-style/shoelace/dist/components/button/button'
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button'
import '@shoelace-style/shoelace/dist/components/icon/icon'
import '@shoelace-style/shoelace/dist/components/tag/tag'

import { marked } from 'marked'
marked.setOptions({ breaks: true })

type InputType = 'text' | 'audio' | 'image'
type Input     = {type: InputType, content: string}
type Output    = {json: Object|null, text: string}
type Review    = { sender: string, rating: number, comment?: string }
type Attachment= {uid: string, type: string, title: string, content: string, path: string, date: string|Date, tags: Array<string>}
type Exchange  = {uid: string, input?: Input, output?: Output, attachments?: Attachment[], timestamp: string|Date, status: string, review?: Review}
type ChunkType = 'start' | 'error' | 'searching' | 'thinking' | 'topic' | 'attachments' | 'data' | 'token'
type Chunk     = {uid: string, type: ChunkType,  data: any, last: boolean }

const Default = {
    Header: 'Chat BOT',
    Footer: 'powered by <b>VOXYMORE</b>',
    Invite: 'ask anything..',
    Styles: {}
}

const toSafeHTML = (md: string) => unsafeHTML(marked.parse(md) as string) 

@customElement('vxc-searchbot')
export class TSearchBot extends LitElement {
    private engine : TIAmie|null = null
    static styles = [
        GridCSS,
        css`
            :host {
                margin: 0;
                padding: 0;
                min-height: 100vh;
                width: 100%;
                background: white;
            }

            .container {
                margin-bottom: 1rem;
                overflow: auto;
            }

            #main {
                display: flex;
                flex-direction: column;
                height: 100vh;
                align-items: center;
                background: white;
            }

            #main > div {
                padding: 1em;
                margin: 0.5em 0;
                text-align: center;
                overflow: auto;
            }

            .q {
                justify-content: center;
            }

            .qna {
                justify-content: flex-start;
            }

            #answer {
                background-color: #f3f5f6;
                boder: 1px solid #CCC;
                border-radius: 20px;
                margin-top: 1rem;
                margin-bottom: 1rem;
                text-align: left;
                padding: 1rem;
            }

            #answer ul {
                padding-left: 2rem;
            }

            #answer ul li::marker {
                content: "→ "; 
                color: blue;
                font-size: 1.2em;
            }

            #docs {
                margin-top: 1rem;
                margin-bottom: 1rem;
                text-align: left;
                padding: 1rem;            
            }

            .result {
                margin-bottom: 2rem;
            }

            .result .title {
                color: blue;
                font-size: 1rem;
            }

            .result .date {
                font-size: .65rem;
                color: grey;
            }

            .tags sl-tag {
                margin: .5rem .5rem 0 0
            }

        `]

    /** states */
    @state() private sid  : String | null = null  
    @state() private exchange : Exchange | null = null

    /** properties */
    @property({ type: String                            })     host: string = ''
    @property({ type: String                            })     uid: string = ''
    @property({ type: Object, attribute: 'data-session' })     sessionData: Record<string,any> = {}
    @property({ type: String                    })     invite: string = Default.Invite
    
    /** queries */
    @query('#user-input') private question!: HTMLInputElement

    constructor() {
        super()
    }

    updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has('uid') && this.uid?.length) {
            this.loadApp()
        }
        super.updated(changedProperties)
    }

    private async loadApp() {
        try {
            console.log('loading app')
            this.engine = null
            this.engine = new TIAmie(this.uid, this.sessionData, this.host)
        } 
        catch (e) { console.error('Failed to load application', e) }
    } 

    private async createSession() {
        try {
            if(this.engine) {
                console.log("creating session")
                var sid = sessionStorage.getItem('voxy-ai-chat-session-uid')
                var session = await this.engine.start(sid)
                if(!session?.uid) throw new Error('session failed to start..')
                console.log('session :', session.uid)
                this.sid = session.uid
                this.exchange = null
            }
        }
        catch(e) { console.error(e) }
    }

    private processChunk(chunk:any){
        try {
            console.log('got Chunk', chunk)
            this.addChunk(chunk)
        }
        catch(e) { console.error(e) }
    }

    public addChunk(chunk: Chunk) {
        try {
            // {uid: string, input?: Input, output?: Output, attachments?: Attachment[], timestamp: string|Date, status?: string, review?: Review}
            var exchange = this.exchange || { uid: '', timestamp: new Date(), status: '' }
            exchange.uid = chunk.uid
            exchange.output = exchange.output || { json: null, text: '' } 
            switch(chunk.type) {
                case 'start':
                    exchange.status = 'started'
                    break
                case 'searching':
                    exchange.status = 'searching'
                    break
                case 'attachments':
                    exchange.attachments = chunk.data as Attachment[]
                    break;
                case 'thinking':
                    exchange.status = 'thinking'
                    break
                case 'topic':
                    break;
                case 'error':
                    exchange.status = 'error'
                    break;
                case 'token':
                    exchange.status = 'answering'
                    exchange.output.text += chunk.data
                    break;
            }
            if(chunk.last)      exchange.status = 'done'
            this.exchange = {...exchange}
        }
        catch(e) { console.error(e) }
    }

    private async sendQuestion() {
        try {
            console.log('send question')
            var self = this
            let q = this.question?.value
            this.question.value = ''
            // console.log('question', this.question)
            // console.log('value', this.question.value)
            if(q?.length) {
                this.exchange = { uid: '', input: { type: 'text', content: q }, timestamp: new Date(), status: '' }
                // console.log('sending question :', q)
                // this.conversation.createExchange({type: 'text', content: q})
                await this.createSession()  
                await this.engine?.ask(q, 
                    (chunk) => self.processChunk(chunk), 
                    () => console.log('done'), 
                    (e) => console.error(e) )
            }
        }
        catch(e) { console.error(e) }
    }

    private handleKeyPress(e: KeyboardEvent) {
        if (e.key === 'Enter') this.sendQuestion()
    }

    private renderAttachment(att: Attachment) {
        return html`
            <div class="result">
                <div class="title">${att.title}</div>
                <div class="date">${new Date(att.date).toLocaleString('fr-FR')}</div>
                <div class="content">
                    ${att.content.replace(/\n/g, '... ').replace(/\*\*/g, '').substring(0, 500)+'...'}
                </div>
                <div class="tags">
                    ${(att.tags||[]).map(tag => html`<sl-tag size="small">${tag}</sl-tag>`)}
                </div>
            </div>
        `
    }

    /**
     * render function
     * @returns html
     */
    render() {
        // console.log('chat bot visible', this.show)
        console.dir(this.exchange, {depth: null})
        const classes = {
            'q'  : this.sid === null,
            'qna': this.sid != null
        }
        const dom = html`
        <div id="main" class=${classMap(classes)}>
            <div class="container">
                <div class="row">
                    <div class="col">
                        <div style="padding: 5px; min-height: 8rem;">    
                        <sl-input id="user-input" placeholder="Large" size="large" placeholder="${this.invite || 'Type your message...'}" dir="auto" @keypress=${this.handleKeyPress} pill>
                            <sl-icon-button name="wizard" library="voxy-icons" slot="suffix" @click=${this.sendQuestion} style="margin-right: 1rem; font-size: 2rem"></sl-icon-button>
                        </sl-input>
                        </div>
                    </div>
                </div>
            </div>
            ${this.exchange? html`
            <div class="container">
                <div class="row">
                    <div id="docs" class="col-12 col-lg-8">
                        ${this.exchange.attachments?.map( att => this.renderAttachment(att))}
                    </div>
                    <div class="col-12 col-lg-4">
                        <div id="answer">
                            ${
                                ['thinking', 'searching'].includes(this.exchange.status) ? 
                                html `<span class='waiting'>${this.exchange.status}</span>` : 
                                html `${toSafeHTML(this.exchange.output?.text||'')}`
                            }
                        </div>
                    <div>
                </div>
            </div>` : ''}
        </div>
        `
        // console.dir(this.dialog)
        return dom
    }
}

declare global {
  interface HTMLElementTagNameMap {
    'vxc-searchbot': TSearchBot;
  }
}